// Purchasing Request — list + create (Phase 1E-δ Batch 1)
// State: DRAFT → SUBMITTED → APPROVED / REJECTED / CANCELLED
// Role tuple backend:
//   PR_WRITE_ROLES = KEPALA_CABANG, ADMIN_GUDANG  (redesign 2026-07-29)
//                    Owner SENGAJA dikecualikan — hanya 2 role ini yang
//                    boleh membuat/mengajukan PR.
//   PR_READ_ROLES  = + OWNER, ADMIN_PURCHASING, ADMIN_PRODUKSI, CS, FINANCE
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { StatusBadge, PURCHASE_REQUEST_STATUS_TONE } from "../../components/etams/StatusBadge";
import { BusyButton } from "../../components/etams/BusyButton";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { ClipboardList, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDateTime } from "../../lib/format";
import { useStoreTimezones } from "../../lib/timezone";
import {
  listPRs, createPR, apiErrorMessage,
} from "../../lib/purchasingApi";
import Hint from "../../components/etams/Hint";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariBahan, labelBahan } from "../../lib/sumberPilihan";
import {
  BELUM_SELESAI, paramStatus, saringanStatus, saringanDari, saringanSampai,
} from "../../lib/saringanPurchasing";

// `material` = objek bahan terpilih dari PemilihCari (satuan dibaca darinya).
const EMPTY_ITEM = { material_id: "", material: null, qty_requested: "", estimated_unit_price: "", notes: "" };

//: Cermin `URUT_SAH_PR` di backend/services/pr_service.py.
const URUT_SAH = ["requested_at", "pr_number", "status", "total_estimated_amount", "submitted_at", "created_at"];
//: Status PR untuk kotak saringan — dari peta tone bersama (PR tidak punya /meta).
const META_PR = {
  statuses: Object.entries(PURCHASE_REQUEST_STATUS_TONE).map(([code, v]) => ({ code, label: v.l })),
};

export default function PurchaseRequests() {
  const { roleCode, profile } = useAuth();
  const { tzForRegion } = useStoreTimezones();
  const navigate = useNavigate();
  const canWrite = ["KEPALA_CABANG", "ADMIN_GUDANG"].includes(roleCode);
  const isOwner = roleCode === "OWNER";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [regions, setRegions] = useState([]);
  const [stores, setStores] = useState([]);

  // POLA DAFTAR SERAGAM (28 Sep 2026; docs/POLA_DAFTAR.md): saringan bawaan
  // "Belum selesai" (Draf + Diajukan) terlihat sebagai chip dan bisa dihapus.
  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "requested_at", arah: "desc" },
    saringan: {
      status: saringanStatus(META_PR),
      dari: saringanDari(),
      sampai: saringanSampai(),
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const statusFilter = daftar.nilaiSaringan.status;
  const bangunParams = useCallback((ovr = {}) => ({
    ...paramStatus(paramSaringan),
    ...(cari && { q: cari }),
    limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listPRs(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ region_id: "", store_id: "", notes: "" });
  const [rows, setRows] = useState([{ ...EMPTY_ITEM }]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listPRs(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat daftar PR."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/regions").then(r => setRegions(r.data || [])).catch((e) => { setRegions([]); toast.error(apiErrorMessage(e, "Daftar wilayah gagal dimuat — muat ulang halaman.")); });
    api.get("/stores").then(r => setStores(r.data || [])).catch((e) => { setStores([]); toast.error(apiErrorMessage(e, "Daftar cabang gagal dimuat — muat ulang halaman.")); });
    // Bahan TIDAK lagi dimuat seluruhnya ke browser — tiap baris memakai PemilihCari
    // (cari di server, paling banyak 20 hasil). Dulu: seluruh ±260 bahan aktif per buka layar.
  }, []);

  const regionName = (id) => regions.find(r => r.id === id)?.name || "—";

  const openCreate = () => {
    // Non-Owner terikat region sendiri; Owner memilih bebas.
    setForm({ region_id: isOwner ? "" : (profile?.region_id || ""), store_id: "", notes: "" });
    setRows([{ ...EMPTY_ITEM }]);
    setDialogOpen(true);
  };

  const setRow = (i, patch) =>
    setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows(rs => [...rs, { ...EMPTY_ITEM }]);
  const removeRow = (i) => setRows(rs => (rs.length === 1 ? rs : rs.filter((_, idx) => idx !== i)));

  const estTotal = rows.reduce((sum, r) => {
    const q = Number(r.qty_requested) || 0;
    const p = Number(r.estimated_unit_price) || 0;
    return sum + q * p;
  }, 0);

  const save = async () => {
    if (!form.region_id) { toast.error("Wilayah wajib dipilih."); return; }
    // K101: tempat produksi wajib. Ditangkap di sini supaya orangnya tahu
    // SEKARANG, bukan lima langkah kemudian di meja penerimaan barang —
    // itu persis yang terjadi pada PO-202608-1866.
    if (!form.store_id) {
      toast.error("Tempat produksi wajib dipilih — PR hanya bisa diajukan dari Surabaya atau Samarinda.");
      return;
    }
    const clean = rows.filter(r => r.material_id && Number(r.qty_requested) > 0);
    if (clean.length === 0) {
      toast.error("Minimal satu item dengan material & qty > 0.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        region_id: form.region_id,
        store_id: form.store_id,
        notes: form.notes || null,
        items: clean.map(r => {
          const mat = r.material;
          return {
            material_id: r.material_id,
            qty_requested: Number(r.qty_requested),
            unit_of_measure: mat?.unit_of_measure || null,
            estimated_unit_price: r.estimated_unit_price === ""
              ? null : Number(r.estimated_unit_price),
            notes: r.notes || null,
          };
        }),
      };
      const r = await createPR(body);
      toast.success("PR berhasil dibuat sebagai draf.");
      setDialogOpen(false);
      if (r.data?.id) navigate(`/purchasing/pr/${r.data.id}`);
      else load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal membuat PR."));
    } finally {
      setSaving(false);
    }
  };

  const adaSaringan = !!daftar.cari.trim() || daftar.saringanAktif.length > 0;
  const KOLOM_CSV = [
    { kunci: "pr_number", judul: "Nomor PR" },
    { kunci: "region", judul: "Wilayah", ambil: (pr) => regionName(pr.region_id) },
    { kunci: "status", judul: "Status", ambil: (pr) => PURCHASE_REQUEST_STATUS_TONE[pr.status]?.l || pr.status },
    { kunci: "total_estimated_amount", judul: "Estimasi" },
    { kunci: "requested_at", judul: "Diajukan", ambil: (pr) => formatDateTime(pr.submitted_at || pr.requested_at, tzForRegion(pr.region_id)) },
  ];
  // K101: PR hanya dari tempat produksi. Penyaringnya `is_production_capable`
  // di data, BUKAN nama store di kode — S14 melarang region/store hardcoded
  // supaya buka cabang berikutnya cukup mengisi setelan.
  //
  // `=== true` bukan truthy: kolom yang hilang atau null nol boleh dibaca
  // sebagai izin. Backend sudah menolaknya juga (enforce_pr_store); dua lapis,
  // karena menyembunyikan pilihan di layar bukan keamanan.
  const storeOptions = stores.filter(s =>
    (!form.region_id || s.region_id === form.region_id) &&
    s.is_production_capable === true
  );

  return (
    <div className="space-y-6" data-testid="pr-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ClipboardList className="h-6 w-6" /> Permintaan Pembelian
          </h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
            Permintaan pengadaan bahan.
            <Hint>Alur: Draf → Diajukan → Disetujui / Ditolak. Pemohon tidak boleh menyetujui PR-nya sendiri.</Hint>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="pr-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
          </Button>
          {canWrite && (
            <Button size="sm" onClick={openCreate} data-testid="pr-create-btn">
              <Plus className="h-4 w-4 mr-1" /> PR Baru
            </Button>
          )}
        </div>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari nomor PR atau catatan…" testId="pr-palang"
                    ekspor={{ nama: "permintaan_pembelian", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={statusFilter} data-testid="pr-status-filter" aria-label="Saring status"
                onChange={(e) => daftar.setSaringan("status", e.target.value)}
                className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value="">Semua status</option>
          <option value={BELUM_SELESAI}>Belum selesai</option>
          {META_PR.statuses.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
        </select>
        <label className="flex gap-1 items-center text-sm text-muted-foreground">
          Dari
          <Input type="date" value={daftar.nilaiSaringan.dari} data-testid="pr-date-from"
                 onChange={(e) => daftar.setSaringan("dari", e.target.value)} className="h-9 w-auto" />
        </label>
        <label className="flex gap-1 items-center text-sm text-muted-foreground">
          sampai
          <Input type="date" value={daftar.nilaiSaringan.sampai} data-testid="pr-date-to"
                 onChange={(e) => daftar.setSaringan("sampai", e.target.value)} className="h-9 w-auto" />
        </label>
      </PalangDaftar>

      <Card className="overflow-hidden" data-testid="pr-table">
        {loading ? (
          <div className="p-8 space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : daftar.tampil.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm space-y-3">
            {adaSaringan ? (
              <p>Tidak ada PR yang cocok dengan pencarian atau saringan ini. Coba hapus saringannya.</p>
            ) : (
              <>
                <p>Belum ada Permintaan Pembelian (PR). PR adalah permintaan pengadaan bahan yang diajukan Kepala Cabang atau Admin Gudang.</p>
                {canWrite ? (
                  <Button size="sm" onClick={openCreate} data-testid="pr-empty-create">
                    <Plus className="h-4 w-4 mr-1" /> PR Baru
                  </Button>
                ) : (
                  <p>Role Anda hanya dapat melihat PR — pembuatan dilakukan Kepala Cabang / Admin Gudang.</p>
                )}
              </>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="pr_number" className="px-4 py-3 font-medium">Nomor PR</ThUrut>
                <th className="px-4 py-3 font-medium">Wilayah</th>
                <ThUrut daftar={daftar} kunci="status" className="px-4 py-3 font-medium">Status</ThUrut>
                <ThUrut daftar={daftar} kunci="total_estimated_amount" align="right" className="px-4 py-3 font-medium text-right">Estimasi</ThUrut>
                <ThUrut daftar={daftar} kunci="requested_at" className="px-4 py-3 font-medium">Diajukan</ThUrut>
              </tr>
            </thead>
            <tbody>
              {daftar.tampil.map((pr) => {
                const b = PURCHASE_REQUEST_STATUS_TONE[pr.status] || { l: pr.status, tone: "neutral" };
                return (
                  <tr key={pr.id}
                      className="border-b hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/purchasing/pr/${pr.id}`)}
                      data-testid={`pr-row-${pr.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{pr.pr_number}</td>
                    <td className="px-4 py-3">{regionName(pr.region_id)}</td>
                    <td className="px-4 py-3"><StatusBadge tone={b.tone}>{b.l}</StatusBadge></td>
                    <td className="px-4 py-3 text-right">{formatIDR(pr.total_estimated_amount)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatDateTime(pr.submitted_at || pr.requested_at, tzForRegion(pr.region_id))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Paginasi daftar={daftar} testId="pr-paginasi" />
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl" data-testid="pr-create-dialog">
          <DialogHeader>
            <DialogTitle>PR Baru</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm">Wilayah <span aria-hidden="true">*</span></span>
                <select value={form.region_id} data-testid="pr-region"
                        onChange={(e) => setForm({ ...form, region_id: e.target.value, store_id: "" })}
                        disabled={!isOwner && !!profile?.region_id}
                        className="w-full h-9 rounded border px-2 text-sm disabled:bg-muted">
                  <option value="">— pilih wilayah —</option>
                  {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm">Tempat produksi *</span>
                <select value={form.store_id} data-testid="pr-store"
                        onChange={(e) => setForm({ ...form, store_id: e.target.value })}
                        className="w-full h-9 rounded border px-2 text-sm">
                  <option value="">— pilih tempat produksi —</option>
                  {storeOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Item</span>
                <Button variant="outline" size="sm" onClick={addRow} data-testid="pr-add-row">
                  <Plus className="h-4 w-4 mr-1" /> Tambah Baris
                </Button>
              </div>
              <div className="space-y-2" data-testid="pr-items-editor">
                {rows.map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-start">
                    {/* Bahan dicari di server (≥2 huruf), bukan dimuat seluruhnya. */}
                    <PemilihCari className="col-span-5" value={r.material}
                                 onChange={(m) => setRow(i, { material: m || null, material_id: m?.id || "" })}
                                 cari={cariBahan} label={labelBahan}
                                 keterangan={(m) => m?.unit_of_measure || ""}
                                 placeholder="— pilih bahan —" placeholderCari="Ketik kode atau nama bahan…"
                                 testId={`pr-item-material-${i}`} />
                    <Input className="col-span-2" type="number" min="0" step="any"
                           placeholder="Qty" value={r.qty_requested}
                           data-testid={`pr-item-qty-${i}`}
                           onChange={(e) => setRow(i, { qty_requested: e.target.value })} />
                    <Input className={`col-span-3 ${r.material_id && r.estimated_unit_price === ""
                             ? "border-warning bg-warning-soft" : ""}`}
                           type="number" min="0"
                           placeholder="Estimasi harga satuan *"
                           value={r.estimated_unit_price}
                           data-testid={`pr-item-price-${i}`}
                           onChange={(e) => setRow(i, { estimated_unit_price: e.target.value })} />
                    <div className="col-span-1 text-xs text-muted-foreground pt-2">
                      {r.material?.unit_of_measure || ""}
                    </div>
                    <Button variant="ghost" size="sm" className="col-span-1"
                            onClick={() => removeRow(i)} disabled={rows.length === 1}
                            data-testid={`pr-item-remove-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                Harga boleh menyusul.
                <Hint>PR dibuat sebagai draf, tapi tidak bisa diajukan sebelum semua item punya estimasi harga satuan.</Hint>
              </p>
              <div className="text-right text-sm mt-2">
                Estimasi total: <span className="font-semibold">{formatIDR(estTotal)}</span>
              </div>
            </div>

            <label className="block">
              <span className="text-sm">Catatan</span>
              <Textarea rows={2} value={form.notes} data-testid="pr-notes"
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Batal</Button>
            <BusyButton onClick={save} busy={saving} busyText="Menyimpan..."
                        className="bg-brand-orange text-brand-orange-foreground hover:bg-brand-orange/90"
                        data-testid="pr-save-btn">
              Simpan sebagai Draf
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
